import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';

// Helper function to get counter name
const getCounterName = (counterId, counterNamesData) => {
  if (!counterNamesData || !Array.isArray(counterNamesData)) return 'Unknown';
  const counter = counterNamesData.find(counter => parseInt(counter.id) === parseInt(counterId));
  return counter ? counter.name : 'Unknown';
};

// Helper function to get supplier name
const getSupplierName = (supplierId, supplierNamesData) => {
  if (!supplierNamesData || !Array.isArray(supplierNamesData)) return 'Unknown';
  const supplier = supplierNamesData.find(supplier => parseInt(supplier.id) === parseInt(supplierId));
  return supplier ? supplier.name : 'Unknown';
};

// Helper function to get stock name
const getStockName = (stockId, stockNamesData) => {
  if (!stockNamesData || !Array.isArray(stockNamesData)) return 'Unknown';
  const stock = stockNamesData.find(stock => parseInt(stock.id) === parseInt(stockId));
  return stock ? stock.name : 'Unknown';
};

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US').format(amount)  + " "+"sh";
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Manual table drawing function for PDF
const drawClubInventoryTable = (doc, data, startY) => {
  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;
  const rowHeight = 12;

  // Column widths for club inventory
  const colWidths = [10, 25, 25, 30, 25, 25, 25, 25, 25]; // #, Date, Supplier, Counter, Stock, Qty, Cost, Selling, Total Value

  // Check if we need a new page
  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;
  if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage();
    currentY = margin;
  }

  // Draw table header
  doc.setFillColor(255, 255, 255); // White background
  doc.setTextColor(25, 118, 210); // Blue text for headers
  doc.setFont(undefined, 'bold');

  const headers = ['#', 'Date', 'Supplier', 'Counter', 'Stock Item', 'Qty', 'Cost', 'Selling', 'Total Value'];
  let xPos = margin;

  // Draw header background
  doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

  // Draw header text
  headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
  });

  // Draw header underline
  doc.setDrawColor(25, 118, 210);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

  currentY += rowHeight;

  // Draw table rows
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);

  data.forEach((row, rowIndex) => {
    // Calculate needed height for this row (for multi-line text)
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
      const text = String(cell);
      const maxWidth = colWidths[cellIndex] - 4;
      const lines = splitTextIntoLines(doc, text, maxWidth);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
      doc.addPage();
      currentY = margin;
      
      // Redraw header on new page
      doc.setFillColor(255, 255, 255);
      doc.setTextColor(25, 118, 210);
      doc.setFont(undefined, 'bold');
      
      // Draw header background
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      xPos = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
      });
      
      // Draw header underline
      doc.setDrawColor(25, 118, 210);
      doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
      xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
      
      lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
      });
    });
    
    currentY += actualRowHeight;
  });

  return currentY;
};

// Client-side Excel export for Club Inventory
export const exportClubInventoryToExcel = (inventory, referenceData = {}, filters = {}) => {
  try {
    const { counterNamesData = [], supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = referenceData;

    // Prepare data for Excel
    const excelData = inventory.map((item, index) => ({
      'No.': index + 1,
      'ID': item.id,
      'Stock Date': new Date(item.stock_date).toLocaleDateString(),
      'Supplier': getSupplierName(item.supplier_id, supplierNamesData),
      'Counter': getCounterName(item.counter_id, counterNamesData),
      'Stock Item': getStockName(item.stock_id, stockNamesData),
      'Quantity': item.quantity,
      'Cost Price': formatCurrency(item.cost),
      'Selling Price': formatCurrency(item.selling),
      'Total Cost': formatCurrency(item.quantity * item.cost),
      'Total Selling': formatCurrency(item.quantity * item.selling),
      'Profit Margin': formatCurrency((item.selling - item.cost) * item.quantity),
      'Description': item.description || '',
      'Created Date': new Date(item.created_at).toLocaleDateString(),
      'Updated Date': new Date(item.updated_at).toLocaleDateString()
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Club Inventory');

    // Add filter summary sheet if filters are applied
    if (Object.keys(filters).some(key => filters[key])) {
      const filterSummary = [];
      
      if (filters.counter_id) {
        filterSummary.push(['Counter Filter:', getCounterName(filters.counter_id, counterNamesData)]);
      }
      if (filters.supplier_id) {
        filterSummary.push(['Supplier Filter:', getSupplierName(filters.supplier_id, supplierNamesData)]);
      }
      if (filters.dateFrom) {
        filterSummary.push(['Date From:', new Date(filters.dateFrom).toLocaleDateString()]);
      }
      if (filters.dateTo) {
        filterSummary.push(['Date To:', new Date(filters.dateTo).toLocaleDateString()]);
      }
      
      if (filterSummary.length > 0) {
        const summarySheet = XLSX.utils.aoa_to_sheet([
          ['Club Inventory Export Summary'],
          ['Generated:', new Date().toLocaleString()],
          ['Total Records:', inventory.length],
          [],
          ['Applied Filters:'],
          ...filterSummary
        ]);
        
        XLSX.utils.book_append_sheet(workbook, summarySheet, 'Export Summary');
      }
    }

    // Generate Excel file and download
    const fileName = `club_inventory_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Club Inventory Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// Enhanced PDF export for Club Inventory
export const exportClubInventoryToPDFEnhanced = (inventory, referenceData = {}, filters = {}) => {
  try {
    const { counterNamesData = [], supplierNamesData = [], stockNamesData = [] } = referenceData;
    
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
      title: 'Club Inventory Report',
      subject: 'Club Inventory Export',
      author: companyInfo.name,
      keywords: 'club, inventory, export, report',
      creator: companyInfo.name
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('CLUB INVENTORY REPORT', doc.internal.pageSize.width / 2, 25, { align: 'center' });
    
    // Company/System name
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(companyInfo.name, doc.internal.pageSize.width / 2, 35, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 45;
    
    // Report info in two columns
    const leftColumnX = 14;
    const rightColumnX = doc.internal.pageSize.width / 2 + 20;
    
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, leftColumnX, currentY);
    doc.text(`Total Inventory Items: ${inventory.length}`, rightColumnX, currentY);
    currentY += 8;
    
    // Calculate totals
    if (inventory.length > 0) {
      const totalQuantity = inventory.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0);
      const totalCostValue = inventory.reduce((sum, item) => sum + (parseInt(item.quantity || 0) * parseFloat(item.cost || 0)), 0);
      const totalSellingValue = inventory.reduce((sum, item) => sum + (parseInt(item.quantity || 0) * parseFloat(item.selling || 0)), 0);
      const totalProfit = totalSellingValue - totalCostValue;
      const pendingItems = inventory.filter(item => item.status && item.status !== 'completed').length;
      const completedItems = inventory.filter(item => item.status === 'completed').length;
      
      doc.text(`Total Quantity: ${formatCurrency(totalQuantity)}`, leftColumnX, currentY);
      doc.text(`Pending Items: ${pendingItems}`, rightColumnX, currentY);
      currentY += 8;
      doc.text(`Total Cost Value: ${formatCurrency(totalCostValue)}`, leftColumnX, currentY);
      doc.text(`Completed Items: ${completedItems}`, rightColumnX, currentY);
      currentY += 8;
      doc.text(`Total Selling Value: ${formatCurrency(totalSellingValue)}`, leftColumnX, currentY);
      doc.text(`Total Profit: ${formatCurrency(totalProfit)}`, rightColumnX, currentY);
      currentY += 8;
    }
    
    // Filters applied
    if (filters.counter_id || filters.supplier_id || filters.dateFrom || filters.dateTo) {
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text('Applied Filters:', leftColumnX, currentY);
      currentY += 6;
      
      let filterLines = [];
      if (filters.counter_id) {
        filterLines.push(`• Counter: ${getCounterName(filters.counter_id, counterNamesData)}`);
      }
      if (filters.supplier_id) {
        filterLines.push(`• Supplier: ${getSupplierName(filters.supplier_id, supplierNamesData)}`);
      }
      if (filters.dateFrom) {
        filterLines.push(`• From date: ${filters.dateFrom}`);
      }
      if (filters.dateTo) {
        filterLines.push(`• To date: ${filters.dateTo}`);
      }
      
      // Display filters in two columns if many
      const midPoint = Math.ceil(filterLines.length / 2);
      const leftFilters = filterLines.slice(0, midPoint);
      const rightFilters = filterLines.slice(midPoint);
      
      const maxLines = Math.max(leftFilters.length, rightFilters.length);
      
      for (let i = 0; i < maxLines; i++) {
        if (leftFilters[i]) {
          doc.text(leftFilters[i], leftColumnX + 5, currentY);
        }
        if (rightFilters[i]) {
          doc.text(rightFilters[i], rightColumnX, currentY);
        }
        currentY += 6;
      }
      currentY += 6;
    }
    
    // Prepare table data with selected columns: #, Date, Supplier, Counter, Stock Item, Qty, Cost, Selling, Total Value
    const tableData = inventory.map((item, index) => {
      const stockName = getStockName(item.stock_id, stockNamesData);
      const supplierName = getSupplierName(item.supplier_id, supplierNamesData);
      const counterName = getCounterName(item.counter_id, counterNamesData);
      const totalValue = parseFloat(item.quantity || 0) * parseFloat(item.selling || 0);
      
      return {
        no: (index + 1).toString(),
        date: new Date(item.stock_date).toLocaleDateString(),
        supplier: supplierName,
        counter: counterName,
        stock: stockName,
        quantity: formatCurrency(item.quantity || 0),
        cost: formatCurrency(item.cost || 0),
        selling: formatCurrency(item.selling || 0),
        totalValue: formatCurrency(totalValue)
      };
    });
    
    // Draw the table
    currentY = drawClubInventoryTableEnhanced(doc, tableData, currentY + 10);
    
    // Add footer with page number
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        doc.internal.pageSize.width - 14,
        doc.internal.pageSize.height - 10,
        { align: 'right' }
      );
    }
    
    const fileName = `club_inventory_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Club Inventory PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};

// Helper function to draw club inventory table without autoTable
const drawClubInventoryTableEnhanced = (doc, tableData, startY) => {
  // Table configuration
  const marginLeft = 14;
  const marginRight = 14;
  const pageWidth = doc.internal.pageSize.width;
  const tableWidth = pageWidth - marginLeft - marginRight;
  
  // Column widths for landscape mode - adjusted for 9 columns
  const colWidths = {
    no: 10,
    date: 25,
    supplier: 35,
    counter: 25,
    stock: 40,
    quantity: 20,
    cost: 25,
    selling: 25,
    totalValue: 30
  };
  
  // Calculate total width and adjust if needed
  const totalWidth = Object.values(colWidths).reduce((sum, width) => sum + width, 0);
  if (totalWidth > tableWidth) {
    const scaleFactor = tableWidth / totalWidth;
    Object.keys(colWidths).forEach(key => {
      colWidths[key] = Math.floor(colWidths[key] * scaleFactor);
    });
  }
  
  // Draw table header
  let currentX = marginLeft;
  let currentY = startY;
  
  // Header background
  doc.setFillColor(41, 128, 185); // Blue color
  doc.rect(currentX, currentY, tableWidth, 10, 'F');
  
  // Header text
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, 'bold');
  
  // Column headers
  const headers = ['#', 'Date', 'Supplier', 'Counter', 'Stock Item', 'Qty', 'Cost', 'Selling', 'Total Value'];
  
  currentX = marginLeft + 2;
  doc.text('#', currentX, currentY + 7);
  currentX += colWidths.no;
  
  doc.text('Date', currentX + 2, currentY + 7);
  currentX += colWidths.date;
  
  doc.text('Supplier', currentX + 2, currentY + 7);
  currentX += colWidths.supplier;
  
  doc.text('Counter', currentX + 2, currentY + 7);
  currentX += colWidths.counter;
  
  doc.text('Stock Item', currentX + 2, currentY + 7);
  currentX += colWidths.stock;
  
  doc.text('Qty', currentX + 2, currentY + 7);
  currentX += colWidths.quantity;
  
  doc.text('Cost', currentX + 2, currentY + 7);
  currentX += colWidths.cost;
  
  doc.text('Selling', currentX + 2, currentY + 7);
  currentX += colWidths.selling;
  
  doc.text('Total Value', currentX + 2, currentY + 7);
  
  // Draw rows
  currentY += 12; // Move down for first row
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  
  let rowIndex = 0;
  const rowHeight = 8;
  const pageHeight = doc.internal.pageSize.height;
  
  for (const row of tableData) {
    // Check if we need a new page
    if (currentY > pageHeight - 30) {
      doc.addPage('landscape');
      currentY = 20;
      
      // Redraw header on new page
      doc.setFillColor(41, 128, 185);
      doc.rect(marginLeft, currentY, tableWidth, 10, 'F');
      doc.setFontSize(10);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      
      currentX = marginLeft + 2;
      doc.text('#', currentX, currentY + 7);
      currentX += colWidths.no;
      
      doc.text('Date', currentX + 2, currentY + 7);
      currentX += colWidths.date;
      
      doc.text('Supplier', currentX + 2, currentY + 7);
      currentX += colWidths.supplier;
      
      doc.text('Counter', currentX + 2, currentY + 7);
      currentX += colWidths.counter;
      
      doc.text('Stock Item', currentX + 2, currentY + 7);
      currentX += colWidths.stock;
      
      doc.text('Qty', currentX + 2, currentY + 7);
      currentX += colWidths.quantity;
      
      doc.text('Cost', currentX + 2, currentY + 7);
      currentX += colWidths.cost;
      
      doc.text('Selling', currentX + 2, currentY + 7);
      currentX += colWidths.selling;
      
      doc.text('Total Value', currentX + 2, currentY + 7);
      
      currentY += 12;
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
    }
    
    // Alternate row background
    if (rowIndex % 2 === 0) {
      doc.setFillColor(245, 245, 245);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    doc.rect(marginLeft, currentY - 2, tableWidth, rowHeight + 2, 'F');
    
    // Draw cell borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    
    currentX = marginLeft;
    for (const width of Object.values(colWidths)) {
      doc.line(currentX, currentY - 2, currentX, currentY + rowHeight);
      currentX += width;
    }
    doc.line(currentX, currentY - 2, currentX, currentY + rowHeight); // Last vertical line
    
    // Draw horizontal line at bottom of row
    doc.line(marginLeft, currentY + rowHeight, marginLeft + tableWidth, currentY + rowHeight);
    
    // Draw row data
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    
    currentX = marginLeft + 2;
    
    // Row number
    doc.text(row.no, currentX, currentY + 6);
    currentX += colWidths.no;
    
    // Date
    doc.text(row.date, currentX + 2, currentY + 6);
    currentX += colWidths.date;
    
    // Supplier (truncate if too long)
    const supplierName = row.supplier.length > 20 ? row.supplier.substring(0, 20) + '...' : row.supplier;
    doc.text(supplierName, currentX + 2, currentY + 6);
    currentX += colWidths.supplier;
    
    // Counter (truncate if too long)
    const counterName = row.counter.length > 15 ? row.counter.substring(0, 15) + '...' : row.counter;
    doc.text(counterName, currentX + 2, currentY + 6);
    currentX += colWidths.counter;
    
    // Stock Item (truncate if too long)
    const stockName = row.stock.length > 25 ? row.stock.substring(0, 25) + '...' : row.stock;
    doc.text(stockName, currentX + 2, currentY + 6);
    currentX += colWidths.stock;
    
    // Quantity
    doc.text(row.quantity, currentX + 2, currentY + 6);
    currentX += colWidths.quantity;
    
    // Cost
    doc.text(row.cost, currentX + 2, currentY + 6);
    currentX += colWidths.cost;
    
    // Selling
    doc.text(row.selling, currentX + 2, currentY + 6);
    currentX += colWidths.selling;
    
    // Total Value (color code based on value)
    const totalValue = parseFloat(row.totalValue.replace(/,/g, ''));
    if (totalValue > 1000) {
      doc.setTextColor(0, 150, 0); // Green for high value
    } else if (totalValue < 100) {
      doc.setTextColor(255, 0, 0); // Red for low value
    }
    doc.text(row.totalValue, currentX + 2, currentY + 6);
    doc.setTextColor(0, 0, 0); // Reset to black
    
    currentY += rowHeight + 2;
    rowIndex++;
  }
  
  // Draw top border of table
  doc.setDrawColor(41, 128, 185);
  doc.setLineWidth(0.5);
  doc.line(marginLeft, startY - 2, marginLeft + tableWidth, startY - 2);
  
  return currentY + 10;
};


// Simple PDF export for Club Inventory
export const exportClubInventoryToPDF = (inventory, referenceData = {}, filters = {}) => {
  try {
    const { counterNamesData = [], supplierNamesData = [], stockNamesData = [] } = referenceData;
    
    // Create new PDF document
    const doc = new jsPDF();
    
    // Set document properties
    doc.setProperties({
      title: 'Club Inventory Report',
      subject: 'Club Inventory Export',
      author: companyInfo.name,
      keywords: 'club, inventory, export, report',
      creator: companyInfo.name
    });
    
    // Add title
    doc.setFontSize(18);
    doc.setTextColor(33, 33, 33);
    doc.text('CLUB INVENTORY REPORT', 105, 20, { align: 'center' });
    
    // Add generation details
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 35);
    doc.text(`Total Records: ${inventory.length}`, 14, 42);
    
    // Filters applied
    let currentY = 50;
    if (filters.counter_id || filters.supplier_id || filters.dateFrom || filters.dateTo) {
      let filterText = 'Filters: ';
      const filterParts = [];
      if (filters.counter_id) filterParts.push(`Counter: ${getCounterName(filters.counter_id, counterNamesData)}`);
      if (filters.supplier_id) filterParts.push(`Supplier: ${getSupplierName(filters.supplier_id, supplierNamesData)}`);
      if (filters.dateFrom) filterParts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
      if (filters.dateTo) filterParts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
      filterText += filterParts.join(', ');
      
      doc.text(filterText, 14, currentY);
      currentY += 8;
    }
    
    // Prepare table data
    const tableData = inventory.map((item, index) => [
      (index + 1).toString(),
      new Date(item.stock_date).toLocaleDateString(),
      getSupplierName(item.supplier_id, supplierNamesData),
      getCounterName(item.counter_id, counterNamesData),
      getStockName(item.stock_id, stockNamesData),
      formatCurrency(item.quantity),
      formatCurrency(item.cost),
      formatCurrency(item.selling),
      formatCurrency(item.quantity * item.selling)
    ]);
    
    // Draw the table
    drawClubInventoryTable(doc, tableData, currentY + 10);
    
    // Save PDF
    const fileName = `club_inventory_export_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Club Inventory PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};

// Simple CSV export for Club Inventory
export const exportClubInventoryToCSV = (inventory, referenceData = {}) => {
  try {
    const { counterNamesData = [], supplierNamesData = [], stockNamesData = [] } = referenceData;
    
    const headers = ['ID', 'Stock Date', 'Supplier', 'Counter', 'Stock Item', 'Quantity', 'Cost Price', 'Selling Price', 'Total Value', 'Description'];
    const csvData = inventory.map(item => [
      item.id,
      `"${new Date(item.stock_date).toLocaleDateString()}"`,
      `"${getSupplierName(item.supplier_id, supplierNamesData).replace(/"/g, '""')}"`,
      `"${getCounterName(item.counter_id, counterNamesData).replace(/"/g, '""')}"`,
      `"${getStockName(item.stock_id, stockNamesData).replace(/"/g, '""')}"`,
      item.quantity,
      item.cost,
      item.selling,
      (item.quantity * item.selling).toFixed(2),
      `"${(item.description || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `club_inventory_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Club Inventory CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};

// Ultra Simple PDF export for Club Inventory
export const exportClubInventoryUltraSimplePDF = (inventory, referenceData = {}) => {
  try {
    const { counterNamesData = [], supplierNamesData = [], stockNamesData = [] } = referenceData;
    
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(16);
    doc.text('Club Inventory Report', 20, 20);
    
    // Generation info
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
    doc.text(`Total: ${inventory.length} items`, 20, 37);
    
    let y = 50;
    
    // Simple table header
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('#', 10, y);
    doc.text('Date', 15, y);
    doc.text('Supplier', 35, y);
    doc.text('Counter', 70, y);
    doc.text('Stock', 100, y);
    doc.text('Qty', 135, y);
    doc.text('Cost', 150, y);
    doc.text('Sell', 170, y);
    doc.text('Total', 185, y);
    
    y += 10;
    doc.setFont(undefined, 'normal');
    
    // Table rows
    inventory.forEach((item, index) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
        // Redraw header on new page
        doc.setFont(undefined, 'bold');
        doc.text('#', 10, y);
        doc.text('Date', 15, y);
        doc.text('Supplier', 35, y);
        doc.text('Counter', 70, y);
        doc.text('Stock', 100, y);
        doc.text('Qty', 135, y);
        doc.text('Cost', 150, y);
        doc.text('Sell', 170, y);
        doc.text('Total', 185, y);
        y += 10;
        doc.setFont(undefined, 'normal');
      }

      doc.text((index + 1).toString(), 10, y);
      doc.text(new Date(item.stock_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), 15, y);
      
      // Truncate long names
      const supplierName = getSupplierName(item.supplier_id, supplierNamesData);
      const counterName = getCounterName(item.counter_id, counterNamesData);
      const stockName = getStockName(item.stock_id, stockNamesData);
      
      doc.text(supplierName.length > 15 ? supplierName.substring(0, 12) + '...' : supplierName, 35, y);
      doc.text(counterName.length > 15 ? counterName.substring(0, 12) + '...' : counterName, 70, y);
      doc.text(stockName.length > 15 ? stockName.substring(0, 12) + '...' : stockName, 100, y);
      doc.text(item.quantity.toString(), 135, y);
      doc.text(formatCurrency(item.cost), 150, y);
      doc.text(formatCurrency(item.selling), 170, y);
      doc.text(formatCurrency(item.quantity * item.selling), 185, y);

      y += 7;
    });

    const fileName = `club_inventory_simple_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Club Inventory ultra simple PDF export failed:', error);
    throw new Error('Failed to export PDF: ' + error.message);
  }
};

// Statistics export for Club Inventory
export const exportClubInventoryStats = (inventory, referenceData = {}) => {
  try {
    const { counterNamesData = [], supplierNamesData = [], stockNamesData = [] } = referenceData;
    
    // Calculate statistics
    const stats = {
      totalItems: inventory.length,
      totalQuantity: inventory.reduce((sum, item) => sum + parseInt(item.quantity), 0),
      totalCostValue: inventory.reduce((sum, item) => sum + (parseInt(item.quantity) * parseFloat(item.cost)), 0),
      totalSellingValue: inventory.reduce((sum, item) => sum + (parseInt(item.quantity) * parseFloat(item.selling)), 0),
      totalProfit: 0,
      averageCostPerItem: 0,
      averageSellingPerItem: 0,
      highestValueItem: null,
      lowestValueItem: null
    };
    
    stats.totalProfit = stats.totalSellingValue - stats.totalCostValue;
    stats.averageCostPerItem = stats.totalQuantity > 0 ? stats.totalCostValue / stats.totalQuantity : 0;
    stats.averageSellingPerItem = stats.totalQuantity > 0 ? stats.totalSellingValue / stats.totalQuantity : 0;
    
    // Find highest and lowest value items
    if (inventory.length > 0) {
      let highestValue = 0;
      let lowestValue = Infinity;
      
      inventory.forEach(item => {
        const itemValue = item.quantity * item.selling;
        if (itemValue > highestValue) {
          highestValue = itemValue;
          stats.highestValueItem = {
            item: getStockName(item.stock_id, stockNamesData),
            supplier: getSupplierName(item.supplier_id, supplierNamesData),
            value: itemValue,
            quantity: item.quantity
          };
        }
        if (itemValue < lowestValue) {
          lowestValue = itemValue;
          stats.lowestValueItem = {
            item: getStockName(item.stock_id, stockNamesData),
            supplier: getSupplierName(item.supplier_id, supplierNamesData),
            value: itemValue,
            quantity: item.quantity
          };
        }
      });
    }
    
    // Create statistics report
    const statsReport = [
      'CLUB INVENTORY STATISTICS REPORT',
      `Generated: ${new Date().toLocaleString()}`,
      '',
      'OVERALL STATISTICS:',
      `Total Inventory Items: ${stats.totalItems}`,
      `Total Quantity: ${formatCurrency(stats.totalQuantity)}`,
      `Total Cost Value: ${formatCurrency(stats.totalCostValue)}`,
      `Total Selling Value: ${formatCurrency(stats.totalSellingValue)}`,
      `Total Potential Profit: ${formatCurrency(stats.totalProfit)}`,
      `Average Cost per Item: ${formatCurrency(stats.averageCostPerItem)}`,
      `Average Selling per Item: ${formatCurrency(stats.averageSellingPerItem)}`,
      '',
      'VALUABLE ITEMS:',
      stats.highestValueItem ? `Highest Value: ${stats.highestValueItem.item} (${stats.highestValueItem.supplier}) - ${formatCurrency(stats.highestValueItem.value)}` : 'No items found',
      stats.lowestValueItem ? `Lowest Value: ${stats.lowestValueItem.item} (${stats.lowestValueItem.supplier}) - ${formatCurrency(stats.lowestValueItem.value)}` : 'No items found'
    ];
    
    // Create CSV for statistics
    const csvContent = statsReport.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `club_inventory_stats_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, stats };
  } catch (error) {
    console.error('Club Inventory stats export failed:', error);
    throw new Error('Failed to export statistics: ' + error.message);
  }
};


